<script setup>
import {
	ref,
	defineProps,
	defineEmits,
	computed,
	watch,
	onMounted,
	onBeforeUnmount,
} from 'vue';
import { $api } from '@/services';

const props = defineProps({
	menuDetail: {
		type: Object,
		required: true,
	},
	dow: {
		type: Number,
		required: true,
	},
	openModal: {
		type: Boolean,
		required: true,
	},
});
const listDishes = ref([]);
onMounted(() => {
	document.addEventListener('click', handleClickOutside);
});

onBeforeUnmount(() => {
	document.removeEventListener('click', handleClickOutside);
});
watch(
	() => props.openModal,
	(value) => {
		if (value) {
			getDishes();
		}
	},
	{ immediate: true }
);

async function getDishes(search) {
	try {
		const res = await $api.menu.listDishes(search);
		listDishes.value = res.data.data;
	} catch (error) {
		console.log(error);
	}
}
const activeInput = ref(null);
const timeout = ref(null);
function searchDish(e) {
	clearTimeout(timeout.value);
	timeout.value = setTimeout(() => {
		getDishes(e.target.value);
	}, 300);
}
function handleClickOutside() {
	activeInput.value = null;
}

function selectDish(dish, mealId, dishIndex) {
	menuDetail.value.menu_details.forEach((mealGroup) => {
		mealGroup.menu_details.forEach((dayGroup) => {
			dayGroup.menu_details.forEach((detail) => {
				if (detail.id === mealId) {
					detail.menu_item_dishes[dishIndex].dish.name = dish.name;
					detail.menu_item_dishes[dishIndex].dish_id = dish.id;
				}
			});
		});
	});
	activeInput.value = null;
	getDishes('');
}

const menuDetail = ref(JSON.parse(JSON.stringify(props.menuDetail)));

const emit = defineEmits(['close', 'success']);

const close = () => {
	emit('close');
};

const deleteItem = (mealId, dishIndex) => {
	menuDetail.value.menu_details.forEach((mealGroup) => {
		mealGroup.menu_details.forEach((dayGroup) => {
			dayGroup.menu_details.forEach((detail) => {
				if (detail.id === mealId) {
					if (
						detail.menu_item_dishes &&
						detail.menu_item_dishes.length
					) {
						detail.menu_item_dishes.splice(dishIndex, 1);
					}
				}
			});
		});
	});
};

const addItem = (mealId) => {
	menuDetail.value.menu_details.forEach((mealGroup) => {
		mealGroup.menu_details.forEach((dayGroup) => {
			dayGroup.menu_details.forEach((detail) => {
				if (detail.id === mealId) {
					if (!detail.menu_item_dishes) {
						detail.menu_item_dishes = [];
					}
					detail.menu_item_dishes.push({
						dish: {
							name: '',
						},
					});
				}
			});
		});
	});
};

const mealsByDow = computed(() =>
	menuDetail.value.menu_details
		.flatMap((mealGroup) =>
			mealGroup.menu_details
				.filter((d) => d.day_of_week === props.dow)
				.map((d) => d.menu_details[0])
		)
);

function formatDate(date) {
	if (!date) return '';
	return date.split('-').reverse().join('/');
}

async function updateMenuByDay() {
	try {
		await $api.menu.updateMenuByDay(menuDetail.value.id, {
			day_of_week: props.dow,
			menu_id: menuDetail.value.id,
			menu_details: mealsByDow.value.map((meal) => ({
				menu_item_dishes: meal.menu_item_dishes.map((dish) => ({
					menu_detail_id: meal.id,
					dish: { name: dish.dish.name },
					dish_id: dish.dish_id,
				})),
			})),
		});
		emit('success');
	} catch (error) {
		console.log(error);
	}
}

function getDateFromWeek(weekStartStr, input) {
	const date = new Date(weekStartStr);
	date.setDate(date.getDate() + (input - 1));
	const y = date.getFullYear();
	const m = String(date.getMonth() + 1).padStart(2, '0');
	const d = String(date.getDate()).padStart(2, '0');
	return `${d}/${m}/${y}`;
}
</script>
<template>
	<div class="fixed inset-y-0 right-0 flex side-over z-50 w-screen md:w-2/5">
		<div
			class="h-full w-full flex flex-col bg-white shadow-xl overflow-y-auto"
		>
			<div class="px-6 py-8 bg-blue-50">
				<div class="flex items-center justify-between gap-1">
					<div class="flex items-center">
						<img
							src="@/assets/images/icons/pen.png"
							alt="edit"
							class="w-6 h-6"
						/>
						<div class="text-2xl font-bold text-neutral-700 ml-2">
							Chỉnh sửa thực đơn theo ngày
						</div>
					</div>
					<div class="cursor-pointer" @click="close">
						<svg
							class="h-6 w-6"
							xmlns="http://www.w3.org/2000/svg"
							fill="none"
							viewBox="0 0 24 24"
							stroke="currentColor"
							aria-hidden="true"
						>
							<path
								stroke-linecap="round"
								stroke-linejoin="round"
								stroke-width="2"
								d="M6 18L18 6M6 6l12 12"
							/>
						</svg>
					</div>
				</div>
			</div>
			<div
				class="sideover-content mt-6 relative flex-1 px-4 sm:px-6"
				style="scroll-behavior: smooth"
				id="slide-over"
			>
				<div class="text-neutral-600 mb-4">
					<span class="font-semibold"
						>{{ dow + 1 !== 8 ? `Thứ ${dow + 1}` : 'Chủ Nhật' }},
						{{ getDateFromWeek(menuDetail?.start_date, dow) }}</span
					>
					- ({{ formatDate(menuDetail?.start_date) }} -
					{{ formatDate(menuDetail?.end_date) }})
				</div>
				<div class="mb-2" v-for="meal in mealsByDow" :key="meal.id">
					<div class="text-neutral-600 font-semibold mb-1">
						{{
							menuDetail.meals.find((m) => m.id === meal.meal_id)
								.name
						}}
					</div>
					<div
						class="flex items-center gap-2 w-full mb-2"
						v-for="(dishItem, dishIndex) in meal.menu_item_dishes"
						:key="dishItem.id || dishIndex"
					>
						<div class="input-group w-full relative">
							<input
								v-model="dishItem.dish.name"
								class="bg-white w-full"
								placeholder="Nhập tên món ăn"
								type="text"
								@click.stop="
									activeInput = meal.id + '-' + dishIndex
								"
								@input="searchDish"
							/>
							<div
								v-if="activeInput === meal.id + '-' + dishIndex"
								class="border rounded bg-white shadow absolute top-10 left-0 w-full z-10 overflow-y-auto max-h-40"
							>
								<div v-for="dish in listDishes" :key="dish.id">
									<div
										class="text-neutral-700 font-semibold mb-1 cursor-pointer hover:bg-gray-100 px-2 py-1"
										@click="
											selectDish(dish, meal.id, dishIndex)
										"
									>
										{{ dish.name }}
									</div>
								</div>
							</div>
						</div>
						<div
							class="cursor-pointer flex items-center gap-2 w-max"
							@click="deleteItem(meal.id, dishIndex)"
						>
							<img
								src="@/assets/images/icons/trash-fill-blue.png"
								alt="edit"
								class="w-4 h-4"
							/>
							<div class="text-blue-600 font-semibold text-sm">
								Xoá
							</div>
						</div>
					</div>
					<div
						class="text-right text-yellow-700 font-semibold text-sm cursor-pointer mt-2"
						@click="addItem(meal.id)"
					>
						Thêm món ăn
					</div>
				</div>
			</div>
			<div class="px-6 py-4 border-t border-yellow-50">
				<div class="flex justify-end">
					<div>
						<button
							type="button"
							class="button-p text-center w-32"
							@click="updateMenuByDay"
						>
							Xác nhận
						</button>
					</div>
				</div>
			</div>
		</div>
	</div>
</template>